import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Consent-information widget settings. Mirrors the actions widget's show/hide
// + order pattern, with one twist: the `enabled` consent is pinned on top
// (it's the master consent) and is NOT part of the persisted order key. The
// order key only holds the reorderable sub-consents; at render the pinned
// `enabled` item always leads.
//
// Composite value under one key (`consent_information`), like details_contact:
//   { items: Record<string,boolean>, show_logs: boolean }
// - `items`    : per-consent show/hide (enabled + is_intake, is_measure_weight,
//                is_blood, is_diary_eat, is_diary_move, is_daynote). Pure
//                two-state (shown/hidden), like actions. No module gating, so
//                no 'disabled' state. The sub-consents have a runtime
//                !is_secretary gate in the widget (not here).
// - `show_logs`: widget-level toggle for the "goto consent logs" card-action
//                footer. On by default (preserves the historical always-shown
//                behaviour). Mirrors details_contact's `edit`/`hide_empty`.
// - `order`    : render order of the REORDERABLE sub-consents only. The pinned
//                `enabled` is always first and excluded from this list.
// Hidden consents don't disappear — they move behind a "show all" toggle at
// runtime (same overflow model as the actions widget).

export type ConsentInformationSettingsValue = {
  items?: Record<string, boolean>;
  show_logs?: boolean;
};

type Props = {
  value: ConsentInformationSettingsValue;
  onChange: (v: ConsentInformationSettingsValue) => void;
  order: string[];
  onOrderChange: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.consent_information';

export const CoachSettingsWidgetConsentInformation: FunctionComponent<Props> = ({
  value,
  onChange,
  order,
  onOrderChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const items = value?.items || {};
  const showLogs = value?.show_logs !== false; // default on

  // Resolve items in the (draft) order, falling back to the default order when
  // the draft is empty. getOrderedConsents emits the pinned `enabled` first and
  // tolerates drift in the reorderable sub-consents.
  const orderedItems = HelpersClientWidgets.getOrderedConsents(order, items);
  const pinnedItem = orderedItems.find((i) => i.pinned);
  const reorderableItems = orderedItems.filter((i) => !i.pinned);

  // Helper to update the composite value: each per-consent toggle and the
  // show_logs toggle write back to the same `items`/`show_logs` shape.
  const setItems = (nextItems: Record<string, boolean>) => onChange({ ...value, items: nextItems });

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Note: hidden consents don't disappear — they move behind a "show all"
        toggle on the client detail page. Explained here so coaches know hiding
        is non-destructive (a sub-consent stays reachable even when tucked). */}
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
        {t(HelpersClientWidgets.consentsNoteKey)}
      </Typography>
    </Grid>

    <Grid item xs={12}>
      <Paper variant="outlined" sx={{padding: 1}}>
        {/* Pinned `enabled` consent: toggleable (coach can hide its row), but
            NOT draggable — it always leads. Rendered as a fixed first row with
            a switch only (no drag handle). */}
        {pinnedItem && <ConsentPinnedRow
          item={pinnedItem}
          label={t(`${LABEL_BASE}.items.${pinnedItem.key}`)}
          theme={theme}
          checked={pinnedItem.state === 'shown'}
          onToggle={(checked) => setItems({ ...items, [pinnedItem.key]: checked })}
        />}

        <Divider sx={{my: 0.5}} />

        {/* Reorder + show/hide list for the sub-consents. Drag handle
            reorders; the switch toggles directly-shown vs behind "show all". */}
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // With useSortable, the OptimisticSortingPlugin reorders items by
            // mutating each sortable's `index` during dragover. By dragend the
            // source's new index reflects where it was dropped, so we splice it
            // from its initial index to its new index. We can't rely on
            // event.operation.target here — the plugin re-points it at source.
            // NOTE: indices here are relative to the reorderable list only
            // (the pinned row is outside this DragDropProvider), so they map
            // 1:1 to the order key.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = reorderableItems.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onOrderChange(next);
          }}
        >
          {reorderableItems.map((item, index) => (
            <ConsentSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`${LABEL_BASE}.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'shown'}
              onToggle={(checked) => setItems({ ...items, [item.key]: checked })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>

    {/* Widget-level toggle: consent-logs footer. Mirrors details_contact's
        options block (a Switcher + the same icon the runtime footer uses). */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.consentsShowLogsIcon
            sx={{fontSize: '1.2rem', color: showLogs ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.show_logs`)}
            value={showLogs}
            greying
            onChange={(c) => onChange({ ...value, show_logs: c })}
          />
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Pinned row: switch only, no drag handle. Leading spot is fixed.
type PinnedRowProps = {
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

const ConsentPinnedRow: FunctionComponent<PinnedRowProps> = ({item, label, theme, checked, onToggle}) => {
  const { Icon } = item;
  const stateColor = checked
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        // Reserve the handle column width so the pinned row aligns with the
        // sortable rows below (which have a drag handle on the left).
        paddingLeft: '30px',
      }}
    >
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

type RowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

const ConsentSortableRow: FunctionComponent<RowProps> = ({id, index, item, label, theme, checked, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  // shown -> primary (green), hidden -> secondary (red, behind "show all")
  const stateColor = checked
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

// Read-only status row: one icon per consent in saved order (pinned `enabled`
// leading), coloured by state, + a compact marker for the show_logs toggle.
// primary = shown, secondary = hidden.
type StatusProps = {
  value: ConsentInformationSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetConsentInformationStatus: FunctionComponent<StatusProps> = ({
  value,
  order
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const items = value?.items || {};
  const showLogs = value?.show_logs !== false; // default on

  const orderedItems = HelpersClientWidgets.getOrderedConsents(order, items);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1.5
    }}>
      {orderedItems.map((item) => {
        const { Icon } = item;
        const color = item.state === 'shown'
          ? theme.palette.primary.main
          : theme.palette.secondary.main;
        return <Box
          key={item.key}
          title={t(`${LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} />
        </Box>;
      })}
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.show_logs`)}>
        <HelpersClientWidgets.consentsShowLogsIcon
          sx={{ fontSize: '1.1rem', color: showLogs ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
